import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSite, usePublic } from "../../lib/site";
import { href } from "../../lib/i18n";
import Hero from "../../components/site/Hero";
import NormalHome from "../../components/site/NormalHome";
import { SloganBand, WhyRunning, Priorities, Countdown, VolunteerSection } from "../../components/site/Campaign";
import { NewsCard, AlbumCard } from "../../components/site/Cards";
import { SectionHead, Socials } from "../../components/site/Common";
import { Seo } from "../../components/site/Seo";
import { StyledText } from "../../components/site/StyledText";

const More = ({ to, label, id }) => (
  <Link to={to} data-testid={id} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-s-primary hover:gap-2 transition-[gap]">
    {label} <ArrowUpRight size={16} />
  </Link>
);

function ContactCta() {
  const { site, lang, ui } = useSite();
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="section-contact-cta">
      <div className="rounded-[var(--s-radius-sm)] border border-site-border bg-s-surface p-10 sm:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
        <div>
          <StyledText as="h2" className="font-heading text-3xl sm:text-4xl font-bold" style={site.profile?.section_style} text={ui.reachMe} />
          <p className="mt-4 text-s-muted max-w-lg">{ui.reachText}</p>
          <p className="eyebrow mt-8 mb-3">{ui.follow}</p>
          <Socials socials={site.profile?.socials} />
        </div>
        <Link to={href(lang, "contact")} data-testid="cta-contact-button" className="self-start lg:self-auto rounded-[var(--s-radius)] bg-s-primary text-s-onprimary px-9 py-4 font-semibold hover:-translate-y-0.5 transition-transform">{ui.contact}</Link>
      </div>
    </section>
  );
}

export default function Home() {
  const { site, lang, t, ui } = useSite();
  const { data } = usePublic(["home"], "/public/home");
  const campaign = site.theme?.mode === "adaylik";
  const p = site.profile || {};
  const personLd = { "@context": "https://schema.org", "@type": "Person", name: p.name, jobTitle: site.theme?.mode === "adaylik" ? t(p.title) : t(p.tagline), image: p.photo, url: window.location.origin, sameAs: Object.values(p.socials || {}).filter(Boolean) };
  if (!campaign) return <><Seo jsonLd={personLd} image={p.blog_photo || p.photo} /><NormalHome data={data} /></>;
  return (
    <>
      <Seo jsonLd={personLd} image={p.photo} />
      <Hero />
      {campaign && <><SloganBand /><WhyRunning /><Priorities /><Countdown /></>}
      {data?.news?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="section-latest-news">
          <SectionHead eyebrow={ui.news} title={ui.latestNews} action={<More to={href(lang, "news")} label={ui.allNews} id="home-all-news-link" />} />
          <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-8">
            {data.news.map((n, i) => <NewsCard key={n.id} n={n} big={i === 0} />)}
          </div>
        </section>
      )}
      {data?.albums?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="section-featured-albums">
          <SectionHead eyebrow={ui.videos} title={ui.featuredVideos} action={<More to={href(lang, "videos")} label={ui.allVideos} id="home-all-videos-link" />} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.albums.map((a) => <AlbumCard key={a.id} a={a} />)}</div>
        </section>
      )}
      {campaign && site.campaign?.volunteer_enabled && <VolunteerSection />}
      <ContactCta />
    </>
  );
}
